/**
 * FRIDAY — Client-side task notification scheduler.
 *
 * Web: Service Worker scheduled notifications (works in background tabs).
 * APK: JavaScriptInterface bridge to native AlarmManager (works even when closed).
 *
 * Key fix: Previously used main-thread setTimeout which browsers throttle
 * to ~1/min in background tabs. Now delegates scheduling to the SW which
 * runs in a separate thread with a 30s polling loop.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
let swRegistration = null;

// ── Service Worker registration ──
export async function initNotifications() {
  if (!('serviceWorker' in navigator)) return false;
  try {
    swRegistration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    return true;
  } catch {
    return false;
  }
}

// ── Permission request (call on user gesture only) ──
export async function requestNotificationPermission() {
  // In Android WebView: Notification API doesn't exist, but NativeBridge handles it natively
  if (window.NativeBridge) {
    return window.NativeBridge.hasNotificationPermission() ? 'granted' : 'native-prompt';
  }
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return await Notification.requestPermission();
}

// ── Schedule notifications for today's tasks ──
export async function scheduleToday() {
  clearAllScheduled();

  try {
    const today = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Dhaka', year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(new Date());

    const res = await fetch(`${API_URL}/dashboard/tasks?dateFrom=${today}&dateTo=${today}`);
    const data = await res.json();
    const tasks = data.data || [];

    const now = Date.now();
    let count = 0;

    for (const task of tasks) {
      if (!task.time || task.completedAt) continue;

      // Build due timestamp in Dhaka timezone
      const dueMs = dhakaToMs(task.date, task.time);
      if (!dueMs || dueMs <= now) continue;

      const delay = dueMs - now;
      // Only schedule if due within 24 hours
      if (delay > 24 * 60 * 60 * 1000) continue;

      // Web: schedule via Service Worker (survives background tabs)
      scheduleViaSW(task, dueMs);

      // APK: native AlarmManager via JavaScriptInterface
      scheduleNativeNotification(task, dueMs);

      count++;
    }
    return count;
  } catch {
    return 0;
  }
}

// ── Schedule notification via Service Worker (background-safe) ──
function scheduleViaSW(task, dueMs) {
  if (!swRegistration?.active) return;

  const timeStr = task.time?.slice(0, 5) || '';
  const title = task.title;
  const body = `${(task.type || 'TASK').toUpperCase()} due now${timeStr ? ' at ' + timeStr : ''}${task.description ? ' — ' + task.description : ''}`;

  swRegistration.active.postMessage({
    type: 'SCHEDULE_NOTIFICATION',
    id: task.id,
    title,
    body,
    tag: `task-${task.id}`,
    dueMs,
  });
}

// ── Fire an immediate notification via SW (for /test-notif) ──
async function fireNotification(task) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  const timeStr = task.time?.slice(0, 5) || '';
  const title = task.title;
  const body = `${(task.type || 'TASK').toUpperCase()} due now${timeStr ? ' at ' + timeStr : ''}${task.description ? ' — ' + task.description : ''}`;

  if (swRegistration?.active) {
    try {
      swRegistration.active.postMessage({
        type: 'SHOW_NOTIFICATION',
        title,
        body,
        tag: `task-${task.id}`,
      });
      return;
    } catch { /* fall through */ }
  }

  // Fallback: direct Notification (desktop only)
  try {
    new Notification(title, { body, tag: `task-${task.id}`, icon: '/favicon.svg' });
  } catch { /* mobile doesn't support this */ }
}

// ── Native Android notification via JavaScriptInterface ──
function scheduleNativeNotification(task, dueMs) {
  if (!window.NativeBridge) return;
  const taskJson = JSON.stringify({
    id: task.id,
    title: task.title,
    type: task.type || 'task',
    description: task.description || '',
    dueTimestamp: dueMs,
  });
  try {
    const delayMs = dueMs - Date.now();
    // Short delay (< 10 min): use Handler.postDelayed (reliable, fires on time)
    // Long delay (> 10 min): use AlarmManager.setAlarmClock (survives app close/Doze)
    if (delayMs > 0 && delayMs <= 10 * 60 * 1000 && window.NativeBridge.fireAfterDelay) {
      window.NativeBridge.fireAfterDelay(taskJson, delayMs);
    } else {
      window.NativeBridge.scheduleReminder(taskJson);
    }
  } catch { /* not in WebView */ }
}

// ── Cancel all scheduled notifications ──
function clearAllScheduled() {
  // Tell SW to drop all pending notifications
  if (swRegistration?.active) {
    swRegistration.active.postMessage({ type: 'CLEAR_ALL_NOTIFICATIONS' });
  }
}

// ── Cancel a specific task notification ──
export function cancelNotification(taskId) {
  if (swRegistration?.active) {
    swRegistration.active.postMessage({ type: 'CANCEL_NOTIFICATION', id: taskId });
  }
  if (window.NativeBridge) {
    try { window.NativeBridge.cancelReminder(taskId); } catch {}
  }
}

// ── Convert Dhaka date+time to unix ms ──
function dhakaToMs(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  try {
    const [h, m] = timeStr.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    // Build full ISO string in Dhaka timezone (+06:00)
    const iso = `${dateStr}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00+06:00`;
    const ms = new Date(iso).getTime();
    return isNaN(ms) ? null : ms;
  } catch {
    return null;
  }
}

// ── Test notification (for verification) ──
export async function testNotification() {
  const perm = await requestNotificationPermission();
  const isNative = !!window.NativeBridge;

  if (perm !== 'granted' && !isNative) return { success: false, reason: 'Permission denied' };

  // Web: fire via SW in 3 seconds (only if Notification API exists)
  if ('Notification' in window && Notification.permission === 'granted') {
    const testTask = { id: 99999, title: 'FRIDAY Test', type: 'reminder', time: '', description: 'Notification system working!' };
    setTimeout(() => fireNotification(testTask), 3000);
  }

  // Native: fire directly via Handler (bypasses AlarmManager for test reliability)
  if (isNative) {
    try {
      if (window.NativeBridge.testNotification) {
        window.NativeBridge.testNotification(5000);
      } else {
        // Fallback to AlarmManager if testNotification not available
        window.NativeBridge.scheduleReminder(JSON.stringify({
          id: 99999,
          title: 'FRIDAY Test',
          type: 'reminder',
          description: 'Native notification working!',
          dueTimestamp: Date.now() + 10000,
        }));
      }
    } catch {}
  }

  const method = isNative ? 'Native (AlarmManager)' : 'Web (Service Worker)';
  return { success: true, reason: `Test notification via ${method} will fire in ${isNative ? 5 : 3} seconds` };
}

// ── Check current state ──
export function getNotificationState() {
  const isNative = !!window.NativeBridge;
  const webSupported = 'Notification' in window;
  let permission = 'unsupported';
  if (isNative) {
    try { permission = window.NativeBridge.hasNotificationPermission() ? 'granted' : 'denied'; } catch { permission = 'unknown'; }
  } else if (webSupported) {
    permission = Notification.permission;
  }
  return {
    supported: webSupported || isNative,
    permission,
    swReady: !!swRegistration?.active,
    nativeBridge: isNative,
    scheduled: 'check-via-sw', // SW owns the count now
  };
}
